import React, {
  Fragment,
  useRef,
  useContext,
  useEffect,
  useState
} from 'react';
import classnames from 'classnames';

import type {
  TCaptionTextContentItem
} from '@src/types/wysiwyg_editor';

import TextEditor from '../../wysiwyg_editor/TextEditor';
import { PositionableTextDocument } from '@src/lib/PositionableTextDocument';
import RenderedText from '@src/components/wysiwyg_editor/page_content_items/text/RenderedText';
import { setCaptionText } from '@src/actions/stencils/editor';
import { Portal } from '../../Portal';
import useDomLocationInWysiwygContainer from '../../../hooks/wysiwyg_editor/useDomLocationInWysiwygContainer';
import TextOverflowIndicator from '@src/components/stencils/TextOverflowIndicator';
import { stylesToDefaultMarkValues } from '@src/utils/wysiwyg_editor/styles';
import {
  calculateOverflowContainerLocation,
  calculateTextOverflows
} from '@src/utils/wysiwyg_editor/overflow';
import { isIOS } from 'react-device-detect';
import useOnClickOutside from 'use-onclickoutside';
import ErrorBoundary from '@src/components/ErrorBoundary';
import { emptyDocument } from '@src/utils/proseMirror';
import { defaultMarkValues } from '@src/prose_mirror/schemas/TextSchema';
import { WysiwygEditorStateContext } from '@src/contexts/wysiwyg/editor';

const defaultStyles = {
  color: '#000000',
  size: 15
};

const overflowIndicatorLocation = {
  top: 0,
  right: -35
};

function DefaultText(props: {
  location: any;
  viewbox: any;
  styles: any;
  scaleBasis: {
    height: number;
    width: number;
  };
}) {
  return (
    <tspan
      x={props.location.x * props.viewbox.width}
      y={props.location.y * props.viewbox.height}
      fontFamily={props.styles.face}
      fontSize={
        (props.viewbox.height / props.scaleBasis.height) * props.styles.size
      }
      fontStyle={props.styles.style}
      fontWeight={props.styles.weight}
      fill={props.styles.color || defaultStyles.color}
    >
      (Add your caption)
    </tspan>
  );
}

function CaptionText({ data }: { data: TCaptionTextContentItem }) {
  const domElementsRef = useRef(null);
  const { domScaleBasisDimensions, productionRender, viewbox } = useContext(WysiwygEditorStateContext);
  const containerLocationInDom = useDomLocationInWysiwygContainer(
    data.containerLocation
  );
  const contentLocationInDom = useDomLocationInWysiwygContainer(
    data.contentLocation
  );
  const [showEditor, setShowEditor] = useState(false);
  const [editorContent, setEditorContent] = useState<PositionableTextDocument>(
    data.content
      ? PositionableTextDocument.fromPositionedDocument(data.content)
      : emptyDocument(
          'center',
          stylesToDefaultMarkValues(
            { ...defaultMarkValues, ...defaultStyles },
            {}
          )
        )
  );
  const [textContent, setTextContent] = useState(editorContent.textContent);
  const [textOverflows, setTextOverflows] = useState(false);

  useEffect(() => {
    const newEditorContent = data.content
      ? PositionableTextDocument.fromPositionedDocument(data.content)
      : emptyDocument(
          'center',
          stylesToDefaultMarkValues(
            { ...defaultMarkValues, ...defaultStyles },
            {}
          )
        );

    const newTextContent = newEditorContent.textContent;

    setEditorContent(newEditorContent);
    setTextContent(newTextContent);
    setCaptionText(newTextContent);
  }, [data.content]);

  useEffect(() => {
    setTextOverflows(
      calculateTextOverflows(
        editorContent,
        data.contentLocation.height,
        data.contentLocation.width
      )
    );
  }, [
    data.contentLocation.height,
    data.contentLocation.width,
    setTextOverflows,
    editorContent
  ]);

  useOnClickOutside(domElementsRef, () => setShowEditor(false));

  return (
    <Fragment>
      <g style={{ cursor: showEditor ? 'default' : 'pointer' }}>
        {showEditor || (productionRender && !textContent) ? null : (
          <RenderedText
            editorContent={editorContent}
            maxLines={1}
            textContent={textContent}
            location={data.contentLocation}
            viewbox={viewbox}
          >
            <DefaultText
              location={data.placeholderLocation}
              viewbox={viewbox}
              styles={data.styles}
              scaleBasis={domScaleBasisDimensions}
            />
          </RenderedText>
        )}
      </g>

      <Portal htmlId="wysiwyg-editor-portal">
        <div ref={domElementsRef}>
          {productionRender ? null : textOverflows &&
            !showEditor &&
            containerLocationInDom &&
            contentLocationInDom ? (
            <div
              style={{
                position: 'absolute',
                ...calculateOverflowContainerLocation(
                  overflowIndicatorLocation,
                  containerLocationInDom,
                  contentLocationInDom
                )
              }}
            >
              <div style={{ position: 'relative' }}>
                <TextOverflowIndicator position={{ top: 0, right: 0 }} show />
              </div>
            </div>
          ) : null}

          {!productionRender && containerLocationInDom ? (
            <div
              className={classnames('wysiwyg-editor-canvas-element-editindicatorbox', {
                'wysiwyg-editor-canvas-element-editindicatorbox--active': showEditor
              })}
              onPointerDown={
                // I have no idea why non-ios is requiring a timeout
                isIOS
                  ? () => setShowEditor(true)
                  : () => setTimeout(() => setShowEditor(true), 0)
              }
              style={{
                background: 'transparent',
                cursor: 'pointer',
                height: containerLocationInDom.height * 2.1,
                left: containerLocationInDom.x - 10,
                position: 'absolute',
                top:
                  containerLocationInDom.y -
                  containerLocationInDom.height * 0.5,
                width: containerLocationInDom.width + 20
              }}
            />
          ) : null}

          {contentLocationInDom && containerLocationInDom && showEditor ? (
            <ErrorBoundary
              resetOnChanges={editorContent}
              fallbackComponent={
                <p
                  style={{
                    background: '#ff0000',
                    color: '#ffffff',
                    fontWeight: 'normal',
                    position: 'absolute',
                    top:
                      containerLocationInDom.y + (contentLocationInDom?.y || 0),
                    left:
                      containerLocationInDom.x + (contentLocationInDom?.x || 0),
                    minHeight:
                      contentLocationInDom?.height ??
                      containerLocationInDom.height,
                    width:
                      contentLocationInDom?.width ??
                      containerLocationInDom.width,
                    cursor: 'default'
                  }}
                >
                  There was a problem displaying the text editor. You should{' '}
                  <a
                    onClick={() => {
                      setEditorContent(
                        emptyDocument(
                          'center',
                          stylesToDefaultMarkValues(
                            { ...defaultMarkValues, ...defaultStyles },
                            {}
                          )
                        )
                      );
                    }}
                    style={{
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      color: '#ffffff',
                      fontWeight: 'bold'
                    }}
                  >
                    click here
                  </a>{' '}
                  to reset the content in this text box.
                </p>
              }
            >
              <TextEditor
                defaultAlignment="center"
                initialContent={editorContent}
                location={{
                  height: contentLocationInDom.height,
                  width: contentLocationInDom.width,
                  x: containerLocationInDom.x + contentLocationInDom.x,
                  y: containerLocationInDom.y + contentLocationInDom.y
                }}
                textItem={data}
                localDefaultMarkValues={stylesToDefaultMarkValues(
                  { ...defaultMarkValues, ...defaultStyles },
                  {}
                )}
                overflowIndicatorPosition={overflowIndicatorLocation}
                textOverflows={textOverflows}
                isSingleLine
                focusOnRender
              />
            </ErrorBoundary>
          ) : null}
        </div>
      </Portal>
    </Fragment>
  );
}

export default CaptionText;
